import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { projectTasksAPI, timeLookupsAPI, timeProjectsAPI } from '../services/api';
import { CreateProjectTask, TimeProjectLookup, TimeProjectTaskLookup } from '../types';
import TableLoadingRow from './TableLoadingRow';
import TimeSelect from './TimeSelect';

interface ProjectTaskFormState extends CreateProjectTask {
  projectQuery: string;
}

const defaultForm: ProjectTaskFormState = {
  projectId: 0,
  projectQuery: '',
  title: '',
  description: '',
  isActive: true,
  sortOrder: 0,
};

const ProjectTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<TimeProjectTaskLookup[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [form, setForm] = useState<ProjectTaskFormState>(defaultForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(true);
  const [projectFilterId, setProjectFilterId] = useState<number | undefined>();

  const formatProjectOption = (project: TimeProjectLookup) => `${project.customerTitle} - ${project.title}`;

  const getProjectColor = (projectId: number) => projects.find(project => project.id === projectId)?.color;

  const projectBadge = (label: string, color?: string) => (
    <span className="project-color-badge" style={{ '--project-color': color || '#64748b' } as React.CSSProperties}>
      <span className="project-color-dot" aria-hidden="true" />
      {label}
    </span>
  );

  const filteredTasks = useMemo(() => tasks, [tasks]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectData, taskData] = await Promise.all([
        timeLookupsAPI.getProjects(),
        projectTasksAPI.getTasks(projectFilterId, showInactive),
      ]);
      setProjects(projectData);
      setTasks(taskData);
    } catch {
      toast.error('Project tasks could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, projectFilterId]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setEditingId(null);
    setFormOpen(false);
  };

  const openNewTask = () => {
    setForm({ ...defaultForm });
    setEditingId(null);
    setFormOpen(true);
  };

  const applyProjectQuery = (value: string) => {
    const matchedProject = projects.find(project => formatProjectOption(project) === value);
    setForm(prev => ({
      ...prev,
      projectId: matchedProject?.id || 0,
      projectQuery: value,
    }));
  };

  const editTask = (task: TimeProjectTaskLookup) => {
    setEditingId(task.id);
    setForm({
      projectId: task.projectId,
      projectQuery: `${task.customerTitle} - ${task.projectTitle}`,
      title: task.title,
      description: task.description || '',
      isActive: task.isActive,
      sortOrder: task.sortOrder,
    });
    setFormOpen(true);
  };

  const deactivateTask = async (id: number) => {
    try {
      await projectTasksAPI.deleteTask(id);
      toast.success('Project task deactivated');
      await loadData();
    } catch {
      toast.error('Project task could not be deactivated');
    }
  };

  const updateProjectColor = async (project: TimeProjectLookup, color: string) => {
    try {
      const updatedProject = await timeProjectsAPI.updateProjectColor(project.id, color);
      setProjects(prev => prev.map(item => item.id === updatedProject.id ? updatedProject : item));
      setTasks(prev => prev.map(task => task.projectId === updatedProject.id
        ? { ...task, projectColor: updatedProject.color }
        : task));
      toast.success('Project color updated');
    } catch {
      toast.error('Project color could not be updated');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    const payload: CreateProjectTask = {
      projectId: form.projectId,
      title: form.title,
      description: form.description?.trim() || undefined,
      isActive: form.isActive,
      sortOrder: form.sortOrder,
    };

    setSaving(true);
    try {
      if (editingId) {
        await projectTasksAPI.updateTask(editingId, { ...payload, id: editingId });
        toast.success('Project task updated');
      } else {
        await projectTasksAPI.createTask(payload);
        toast.success('Project task created');
      }
      resetForm();
      await loadData();
    } catch {
      toast.error('Project task could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const taskForm = (
    <form onSubmit={handleSubmit} className="time-form">
      <div className="form-group">
        <label className="form-label" htmlFor="task-project">Project</label>
        <TimeSelect
          inputId="task-project"
          ariaLabel="Project"
          options={projects.map(project => ({ value: project.id, label: formatProjectOption(project) }))}
          value={form.projectId || null}
          onChange={(_, option) => applyProjectQuery(option?.label || '')}
          placeholder="Start typing a project"
          noOptionsMessage="No projects"
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="task-title">Task title</label>
        <input
          id="task-title"
          className="form-control"
          value={form.title}
          onChange={(event) => setForm(prev => ({ ...prev, title: event.target.value }))}
          maxLength={256}
          required
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="task-description">Description</label>
        <textarea
          id="task-description"
          className="form-control"
          value={form.description || ''}
          onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
          rows={3}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="task-sort-order">Sort order</label>
        <input
          id="task-sort-order"
          className="form-control"
          type="number"
          value={form.sortOrder}
          onChange={(event) => setForm(prev => ({ ...prev, sortOrder: Number(event.target.value) }))}
        />
      </div>
      <label className="toggle-row">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
        />
        Active
      </label>
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={!form.projectId || saving} aria-busy={saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          ) : (
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button className="btn btn-secondary" type="button" onClick={resetForm}>
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Project tasks</h1>
          <p>{filteredTasks.length} tasks</p>
        </div>
        <div className="action-row">
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            Show inactive
          </label>
          <button className="btn btn-primary" type="button" onClick={openNewTask}>
            <i className="fa-solid fa-plus" aria-hidden="true" />
            New task
          </button>
        </div>
      </div>

      <section className="card mb-3">
        <div className="card-body">
          <div className="filters-row">
            <div className="form-group filter-project-field">
              <label className="form-label" htmlFor="task-project-filter">Project filter</label>
              <TimeSelect
                inputId="task-project-filter"
                ariaLabel="Project filter"
                options={[
                  { value: 0, label: 'All projects' },
                  ...projects.map(project => ({ value: project.id, label: formatProjectOption(project) })),
                ]}
                value={projectFilterId || 0}
                onChange={(value) => setProjectFilterId(Number(value || 0) || undefined)}
                placeholder="All projects"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="card mb-3">
        <div className="card-header">
          <h5 className="card-title">Project colors</h5>
        </div>
        <div className="card-body">
          <p className="section-subtitle">Colors are used across time tracking to make project data easier to scan.</p>
          <div className="project-color-grid">
            {projects.map(project => (
              <label className="project-color-control" key={project.id}>
                {projectBadge(formatProjectOption(project), project.color)}
                <input
                  aria-label={`Color for ${formatProjectOption(project)}`}
                  type="color"
                  value={project.color || '#64748b'}
                  onChange={(event) => updateProjectColor(project, event.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      </section>

      {isFormOpen && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              resetForm();
            }
          }}
        >
          <section className="time-modal" role="dialog" aria-modal="true" aria-labelledby="project-task-dialog-title">
            <div className="time-modal-header">
              <h5 id="project-task-dialog-title" className="card-title">{editingId ? 'Edit project task' : 'New project task'}</h5>
              <button className="icon-button" type="button" onClick={resetForm} aria-label="Close project task form">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              {taskForm}
            </div>
          </section>
        </div>
      )}

      <section className="card">
        <div className="card-header">
          <h5 className="card-title">Task list</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Task</th>
                  <th>Description</th>
                  <th>Sort</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <TableLoadingRow colSpan={6} label="Loading project tasks..." />}
                {!loading && filteredTasks.map(task => (
                  <tr key={task.id}>
                    <td data-label="Project">{projectBadge(`${task.customerTitle} - ${task.projectTitle}`, task.projectColor || getProjectColor(task.projectId))}</td>
                    <td data-label="Task">{task.title}</td>
                    <td data-label="Description">{task.description || '—'}</td>
                    <td data-label="Sort">{task.sortOrder}</td>
                    <td data-label="Status">{task.isActive ? 'Active' : 'Inactive'}</td>
                    <td data-label="Actions">
                      <div className="action-row">
                        <button className="action-btn" type="button" onClick={() => editTask(task)} title="Edit" aria-label={`Edit ${task.title}`}>
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                        </button>
                        <button className="action-btn danger" type="button" onClick={() => deactivateTask(task.id)} title="Deactivate" aria-label={`Deactivate ${task.title}`}>
                          <i className="fa-solid fa-box-archive" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan={6}><div className="empty-state">No project tasks</div></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectTasksPage;
